import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getApartments, 
  getUsers, 
  getProperties,
  getCollection,
  createDocument,
  updateDocument,
  deleteDocument,
  sendEmail,
  getOrganization,
  Organization
} from '../lib/api';
import { 
  CurrencyCircleDollar, 
  Plus, 
  PaperPlaneTilt, 
  Pencil, 
  Trash,
  Users as UsersIcon,
  Calendar,
  Percent,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  CaretDown,
  CaretRight,
  Download,
  EnvelopeSimple,
  FilePdf,
  Clock,
  Receipt,
  House,
  Bell,
  X
} from '@phosphor-icons/react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FeeTemplate,
  Adjustment,
  SharedCost,
  Invoice,
  FeeFormData,
  AdjustmentFormData,
  SharedCostFormData,
  GenerateFormData,
  Apartment,
  User,
  Property,
  formatCurrency,
  getStatusBadge,
  getFrequencyLabel,
  getDistributionLabel,
  FeeTemplateDialog,
  AdjustmentDialog,
  SharedCostDialog,
  GenerateInvoicesDialog,
  InvoicePreviewDialog,
  PaymentHistoryDialog
} from '../components/invoicing';

export default function InvoicingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invoices');
  const [organization, setOrganization] = useState<Organization | null>(null);
  
  // Data
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [feeTemplates, setFeeTemplates] = useState<FeeTemplate[]>([]);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [sharedCosts, setSharedCosts] = useState<SharedCost[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  
  // Dialogs
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [showSharedCostDialog, setShowSharedCostDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(null);
  
  // Edit state
  const [editingFee, setEditingFee] = useState<FeeTemplate | null>(null);
  const [editingAdjustment, setEditingAdjustment] = useState<Adjustment | null>(null);
  const [editingSharedCost, setEditingSharedCost] = useState<SharedCost | null>(null);
  const [previewInvoices, setPreviewInvoices] = useState<Invoice[]>([]);
  
  // Forms
  const [feeForm, setFeeForm] = useState<FeeFormData>({
    name: '',
    description: '',
    base_amount: '',
    per_sqm_amount: '',
    frequency: 'monthly',
    is_active: true
  });
  
  const [adjustmentForm, setAdjustmentForm] = useState<AdjustmentFormData>({
    name: '',
    description: '',
    type: 'discount',
    amount_type: 'fixed',
    amount: '',
    frequency: 'recurring',
    applies_to: 'all',
    apartment_ids: [],
    start_date: '',
    end_date: '',
    is_active: true
  });
  
  const [sharedCostForm, setSharedCostForm] = useState<SharedCostFormData>({
    name: '',
    description: '',
    total_amount: '',
    distribution_method: 'equal',
    period: new Date().toISOString().slice(0, 7),
    is_included_in_invoice: true
  });
  
  const [generateForm, setGenerateForm] = useState<GenerateFormData>({
    period: new Date().toISOString().slice(0, 7),
    fee_template_id: '',
    include_adjustments: true,
    include_shared_costs: true,
    due_days: '30'
  });

  // Load data
  const loadData = useCallback(async () => {
    if (!user?.organization_id) return;
    
    setLoading(true);
    try {
      const [aptsRes, usersRes, propsRes, feesRes, adjRes, costsRes, invRes, orgRes] = await Promise.all([
        getApartments({ organization_id: user.organization_id }),
        getUsers({ organization_id: user.organization_id }),
        getProperties(),
        getCollection<FeeTemplate>('fee_templates', { organization_id: user.organization_id }),
        getCollection<Adjustment>('fee_adjustments', { organization_id: user.organization_id }),
        getCollection<SharedCost>('shared_costs', { organization_id: user.organization_id }),
        getCollection<Invoice>('invoices', { organization_id: user.organization_id }),
        getOrganization(user.organization_id)
      ]);
      
      setApartments(aptsRes.data || []);
      setUsers(usersRes.data || []);
      setProperties(propsRes.data || []);
      setFeeTemplates(feesRes.data || []);
      setAdjustments(adjRes.data || []);
      setSharedCosts(costsRes.data || []);
      setInvoices(invRes.data || []);
      setOrganization(orgRes.data || orgRes as unknown as Organization || null);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Kunde inte ladda data');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get resident for apartment
  const getResidentForApartment = (apartmentId: string): User | undefined => {
    return users.find(u => u.apartment_id === apartmentId && u.role === 'resident');
  };

  // Fee Template CRUD
  const handleSaveFee = async () => {
    if (!user?.organization_id || !feeForm.name || !feeForm.base_amount) {
      toast.error('Fyll i namn och grundbelopp');
      return;
    }

    try {
      const data = {
        name: feeForm.name,
        description: feeForm.description,
        base_amount: parseFloat(feeForm.base_amount),
        per_sqm_amount: feeForm.per_sqm_amount ? parseFloat(feeForm.per_sqm_amount) : undefined,
        frequency: feeForm.frequency,
        is_active: feeForm.is_active,
        organization_id: user.organization_id
      };

      if (editingFee) {
        await updateDocument('fee_templates', editingFee.id, data);
        toast.success('Avgiftsmall uppdaterad');
      } else {
        await createDocument('fee_templates', data);
        toast.success('Avgiftsmall skapad');
      }
      
      setShowFeeDialog(false);
      setEditingFee(null);
      resetFeeForm();
      loadData();
    } catch (error) {
      toast.error('Kunde inte spara avgiftsmall');
    }
  };

  const handleDeleteFee = async (id: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna avgiftsmall?')) return;
    
    try {
      await deleteDocument('fee_templates', id);
      toast.success('Avgiftsmall borttagen');
      loadData();
    } catch (error) {
      toast.error('Kunde inte ta bort avgiftsmall');
    }
  };

  const resetFeeForm = () => {
    setFeeForm({
      name: '',
      description: '',
      base_amount: '',
      per_sqm_amount: '',
      frequency: 'monthly',
      is_active: true
    });
  };

  // Adjustment CRUD
  const handleSaveAdjustment = async () => {
    if (!user?.organization_id || !adjustmentForm.name || !adjustmentForm.amount) {
      toast.error('Fyll i namn och belopp');
      return;
    }

    try {
      const data = {
        name: adjustmentForm.name,
        description: adjustmentForm.description,
        type: adjustmentForm.type,
        amount_type: adjustmentForm.amount_type,
        amount: parseFloat(adjustmentForm.amount),
        frequency: adjustmentForm.frequency,
        applies_to: adjustmentForm.applies_to,
        apartment_ids: adjustmentForm.applies_to === 'specific' ? adjustmentForm.apartment_ids : undefined,
        start_date: adjustmentForm.start_date || undefined,
        end_date: adjustmentForm.end_date || undefined,
        is_active: adjustmentForm.is_active,
        organization_id: user.organization_id
      };

      if (editingAdjustment) {
        await updateDocument('fee_adjustments', editingAdjustment.id, data);
        toast.success('Justering uppdaterad');
      } else {
        await createDocument('fee_adjustments', data);
        toast.success('Justering skapad');
      }
      
      setShowAdjustmentDialog(false);
      setEditingAdjustment(null);
      resetAdjustmentForm();
      loadData();
    } catch (error) {
      toast.error('Kunde inte spara justering');
    }
  };

  const handleDeleteAdjustment = async (id: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna justering?')) return;
    
    try {
      await deleteDocument('fee_adjustments', id);
      toast.success('Justering borttagen');
      loadData();
    } catch (error) {
      toast.error('Kunde inte ta bort justering');
    }
  };

  const resetAdjustmentForm = () => {
    setAdjustmentForm({
      name: '',
      description: '',
      type: 'discount',
      amount_type: 'fixed',
      amount: '',
      frequency: 'recurring',
      applies_to: 'all',
      apartment_ids: [],
      start_date: '',
      end_date: '',
      is_active: true
    });
  };

  // Shared Cost CRUD
  const handleSaveSharedCost = async () => {
    if (!user?.organization_id || !sharedCostForm.name || !sharedCostForm.total_amount) {
      toast.error('Fyll i namn och totalbelopp');
      return;
    }

    try {
      const data = {
        name: sharedCostForm.name,
        description: sharedCostForm.description,
        total_amount: parseFloat(sharedCostForm.total_amount),
        distribution_method: sharedCostForm.distribution_method,
        period: sharedCostForm.period,
        is_included_in_invoice: sharedCostForm.is_included_in_invoice,
        organization_id: user.organization_id
      };

      if (editingSharedCost) {
        await updateDocument('shared_costs', editingSharedCost.id, data);
        toast.success('Delad kostnad uppdaterad');
      } else {
        await createDocument('shared_costs', data);
        toast.success('Delad kostnad skapad');
      }
      
      setShowSharedCostDialog(false);
      setEditingSharedCost(null);
      resetSharedCostForm();
      loadData();
    } catch (error) {
      toast.error('Kunde inte spara delad kostnad');
    }
  };

  const handleDeleteSharedCost = async (id: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna delade kostnad?')) return;
    
    try {
      await deleteDocument('shared_costs', id);
      toast.success('Delad kostnad borttagen');
      loadData();
    } catch (error) {
      toast.error('Kunde inte ta bort delad kostnad');
    }
  };

  const resetSharedCostForm = () => {
    setSharedCostForm({
      name: '',
      description: '',
      total_amount: '',
      distribution_method: 'equal',
      period: new Date().toISOString().slice(0, 7),
      is_included_in_invoice: true
    });
  };

  // Generate invoices
  const handleGeneratePreview = () => {
    if (!generateForm.fee_template_id) {
      toast.error('Välj en avgiftsmall');
      return;
    }

    const template = feeTemplates.find(f => f.id === generateForm.fee_template_id);
    if (!template) return;

    const totalSqm = apartments.reduce((sum, apt) => sum + (apt.area_sqm || apt.area || 0), 0);
    const periodCosts = sharedCosts.filter(sc => 
      sc.period === generateForm.period && sc.is_included_in_invoice
    );

    const generatedInvoices: Invoice[] = apartments.map((apt, index) => {
      const resident = getResidentForApartment(apt.id);
      const aptSqm = apt.area_sqm || apt.area || 0;
      
      // Calculate base amount
      let baseAmount = template.base_amount;
      if (template.per_sqm_amount && aptSqm > 0) {
        baseAmount += template.per_sqm_amount * aptSqm;
      }

      // Calculate adjustments
      const appliedAdjustments: Invoice['adjustments'] = [];
      if (generateForm.include_adjustments) {
        adjustments
          .filter(adj => adj.is_active)
          .filter(adj => adj.applies_to === 'all' || adj.apartment_ids?.includes(apt.id))
          .forEach(adj => {
            let amount = adj.amount;
            if (adj.amount_type === 'percentage') {
              amount = baseAmount * (adj.amount / 100);
            }
            if (adj.type === 'discount') {
              amount = -Math.abs(amount);
            }
            appliedAdjustments.push({
              name: adj.name,
              type: adj.type,
              amount
            });
          });
      }

      // Calculate shared costs
      const appliedSharedCosts: Invoice['shared_costs'] = [];
      if (generateForm.include_shared_costs) {
        periodCosts.forEach(cost => {
          let share = 0;
          switch (cost.distribution_method) {
            case 'equal':
              share = cost.total_amount / apartments.length;
              break;
            case 'by_sqm':
              share = totalSqm > 0 ? (aptSqm / totalSqm) * cost.total_amount : 0;
              break;
            case 'by_shares':
              // For now, same as equal
              share = cost.total_amount / apartments.length;
              break;
          }
          appliedSharedCosts.push({
            name: cost.name,
            amount: Math.round(share * 100) / 100
          });
        });
      }

      const adjustmentTotal = appliedAdjustments.reduce((sum, adj) => sum + adj.amount, 0);
      const sharedCostTotal = appliedSharedCosts.reduce((sum, sc) => sum + sc.amount, 0);
      const totalAmount = baseAmount + adjustmentTotal + sharedCostTotal;

      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + parseInt(generateForm.due_days));

      return {
        id: `preview-${index}`,
        invoice_number: `${generateForm.period.replace('-', '')}-${String(index + 1).padStart(3, '0')}`,
        apartment_id: apt.id,
        apartment_number: apt.apartment_number,
        resident_name: resident?.name || 'Ej tilldelad',
        resident_email: resident?.email,
        period: generateForm.period,
        base_amount: Math.round(baseAmount * 100) / 100,
        adjustments: appliedAdjustments,
        shared_costs: appliedSharedCosts,
        total_amount: Math.round(totalAmount * 100) / 100,
        status: 'draft',
        due_date: dueDate.toISOString().split('T')[0],
        organization_id: user?.organization_id || '',
        created_at: new Date().toISOString()
      };
    });

    setPreviewInvoices(generatedInvoices);
    setShowGenerateDialog(false);
    setShowPreviewDialog(true);
  };

  const handleCreateInvoices = async () => {
    try {
      for (const invoice of previewInvoices) {
        const { id, ...data } = invoice;
        await createDocument('invoices', data);
      }
      toast.success(`${previewInvoices.length} fakturor skapade`);
      setShowPreviewDialog(false);
      setPreviewInvoices([]);
      loadData();
    } catch (error) {
      toast.error('Kunde inte skapa fakturor');
    }
  };

  const handleSendInvoice = async (invoice: Invoice) => {
    if (!invoice.resident_email) {
      toast.error('Ingen e-postadress för boende');
      return;
    }

    try {
      const monthNames = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 
                        'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
      const [year, month] = invoice.period.split('-');
      const monthName = monthNames[parseInt(month) - 1];

      const htmlContent = `
        <h2>Månadsavi ${monthName} ${year}</h2>
        <p>Hej ${invoice.resident_name},</p>
        <p>Här kommer din månadsavi för ${monthName} ${year}.</p>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Lägenhet</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${invoice.apartment_number}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Grundavgift</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${invoice.base_amount.toLocaleString('sv-SE')} kr</td>
          </tr>
          ${invoice.adjustments.map(adj => `
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${adj.name}</td>
              <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right; color: ${adj.amount < 0 ? 'green' : 'inherit'};">${adj.amount.toLocaleString('sv-SE')} kr</td>
            </tr>
          `).join('')}
          ${invoice.shared_costs.map(sc => `
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #eee;">${sc.name}</td>
              <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${sc.amount.toLocaleString('sv-SE')} kr</td>
            </tr>
          `).join('')}
          <tr style="font-weight: bold;">
            <td style="padding: 12px 8px;">Att betala</td>
            <td style="padding: 12px 8px; text-align: right;">${invoice.total_amount.toLocaleString('sv-SE')} kr</td>
          </tr>
        </table>
        
        <p><strong>Förfallodatum:</strong> ${new Date(invoice.due_date).toLocaleDateString('sv-SE')}</p>
        
        <p style="color: #666; font-size: 14px; margin-top: 30px;">
          Med vänliga hälsningar,<br>
          Styrelsen
        </p>
      `;

      await sendEmail(
        invoice.resident_email,
        `Månadsavi ${monthName} ${year} - ${invoice.apartment_number}`,
        htmlContent,
        { fromName: organization?.name || 'Förvaltningen' }
      );

      // Update invoice status
      await updateDocument('invoices', invoice.id, {
        status: 'sent',
        sent_at: new Date().toISOString()
      });

      toast.success(`Faktura skickad till ${invoice.resident_email}`);
      loadData();
    } catch (error) {
      toast.error('Kunde inte skicka faktura');
    }
  };

  const handleMarkPaid = async (invoice: Invoice) => {
    try {
      await updateDocument('invoices', invoice.id, {
        status: 'paid',
        paid_at: new Date().toISOString()
      });
      toast.success('Faktura markerad som betald');
      loadData();
    } catch (error) {
      toast.error('Kunde inte uppdatera faktura');
    }
  };

  // Generate PDF for invoice
  const generateInvoicePDF = (invoice: Invoice) => {
    const monthNames = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 
                      'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
    const [year, month] = invoice.period.split('-');
    const monthName = monthNames[parseInt(month) - 1];

    // Create PDF
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('MÅNADSAVI', 20, 25);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Fakturanummer: ${invoice.invoice_number}`, 20, 35);
    doc.text(`Period: ${monthName} ${year}`, 20, 42);
    doc.text(`Utfärdat: ${new Date().toLocaleDateString('sv-SE')}`, 20, 49);
    doc.text(`Förfallodatum: ${new Date(invoice.due_date).toLocaleDateString('sv-SE')}`, 20, 56);

    // Recipient info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Mottagare:', 120, 35);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.resident_name || 'Boende', 120, 42);
    doc.text(`Lägenhet ${invoice.apartment_number}`, 120, 49);

    // Divider line
    doc.setLineWidth(0.5);
    doc.line(20, 65, 190, 65);

    // Invoice items table
    const tableData: (string | number)[][] = [];
    
    // Base amount
    tableData.push(['Månadsavgift', '', formatCurrency(invoice.base_amount)]);
    
    // Adjustments
    invoice.adjustments.forEach(adj => {
      const prefix = adj.type === 'discount' ? '  - ' : '  + ';
      tableData.push([prefix + adj.name, '', formatCurrency(adj.amount)]);
    });
    
    // Shared costs
    invoice.shared_costs.forEach(sc => {
      tableData.push(['  + ' + sc.name, '', formatCurrency(sc.amount)]);
    });

    // Use autoTable plugin
    (doc as jsPDF & { autoTable: (options: Record<string, unknown>) => void }).autoTable({
      startY: 75,
      head: [['Beskrivning', '', 'Belopp']],
      body: tableData,
      foot: [['ATT BETALA', '', formatCurrency(invoice.total_amount)]],
      theme: 'plain',
      headStyles: { 
        fillColor: [241, 245, 249], 
        textColor: [30, 41, 59],
        fontStyle: 'bold',
        fontSize: 10
      },
      footStyles: { 
        fillColor: [241, 245, 249], 
        textColor: [30, 41, 59],
        fontStyle: 'bold',
        fontSize: 12
      },
      styles: { 
        fontSize: 10,
        cellPadding: 5
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40, halign: 'right' }
      },
      margin: { left: 20, right: 20 }
    });

    // Payment info
    const finalY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY || 150;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Betalningsinformation:', 20, finalY + 20);
    doc.setFont('helvetica', 'normal');
    doc.text('Bankgiro: 123-4567', 20, finalY + 28);
    doc.text(`OCR: ${invoice.invoice_number.replace(/-/g, '')}`, 20, finalY + 35);
    doc.text(`Förfallodatum: ${new Date(invoice.due_date).toLocaleDateString('sv-SE')}`, 20, finalY + 42);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text('Vid frågor, kontakta styrelsen via Förvaltningen.', 20, 280);

    // Save PDF
    doc.save(`faktura_${invoice.invoice_number}.pdf`);
    toast.success('PDF nedladdad');
  };

  // Check and update overdue invoices
  const checkOverdueInvoices = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const overdueInvoices = invoices.filter(inv => 
      inv.status === 'sent' && inv.due_date < today
    );

    for (const invoice of overdueInvoices) {
      try {
        await updateDocument('invoices', invoice.id, {
          status: 'overdue'
        });
      } catch (error) {
        console.error('Error updating overdue status:', error);
      }
    }

    if (overdueInvoices.length > 0) {
      loadData();
    }
  }, [invoices, loadData]);

  // Check for overdue invoices on load
  useEffect(() => {
    if (invoices.length > 0) {
      checkOverdueInvoices();
    }
  }, [invoices.length]); // Only run when invoices count changes

  // Send reminder for overdue invoice
  const handleSendReminder = async (invoice: Invoice) => {
    if (!invoice.resident_email) {
      toast.error('Ingen e-postadress för boende');
      return;
    }

    try {
      const monthNames = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 
                        'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
      const [year, month] = invoice.period.split('-');
      const monthName = monthNames[parseInt(month) - 1];
      const daysOverdue = Math.floor((new Date().getTime() - new Date(invoice.due_date).getTime()) / (1000 * 60 * 60 * 24));

      const htmlContent = `
        <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #fef2f2; border-left: 4px solid #dc2626; padding: 16px; margin-bottom: 20px;">
            <h2 style="color: #dc2626; margin: 0 0 8px 0;">⚠️ Påminnelse om obetald faktura</h2>
            <p style="margin: 0; color: #991b1b;">Fakturan har förfallit med ${daysOverdue} dagar</p>
          </div>
          
          <p>Hej ${invoice.resident_name},</p>
          <p>Vi vill påminna om att din månadsavi för <strong>${monthName} ${year}</strong> ännu inte är betald.</p>
          
          <table style="width: 100%; border-collapse: collapse; margin: 20px 0; background: #f8fafc; border-radius: 8px;">
            <tr>
              <td style="padding: 12px; border-bottom: 1px solid #e2e8f0;"><strong>Fakturanummer:</strong></td>
              <td style="padding: 12px; border-bottom: 1px solid #e2e8f0; text-align: right;">${invoice.invoice_number}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border-bottom: 1px solid #e2e8f0;"><strong>Lägenhet:</strong></td>
              <td style="padding: 12px; border-bottom: 1px solid #e2e8f0; text-align: right;">${invoice.apartment_number}</td>
            </tr>
            <tr>
              <td style="padding: 12px; border-bottom: 1px solid #e2e8f0;"><strong>Ursprungligt förfallodatum:</strong></td>
              <td style="padding: 12px; border-bottom: 1px solid #e2e8f0; text-align: right;">${new Date(invoice.due_date).toLocaleDateString('sv-SE')}</td>
            </tr>
            <tr style="background: #fee2e2;">
              <td style="padding: 16px; font-size: 18px;"><strong>Att betala:</strong></td>
              <td style="padding: 16px; text-align: right; font-size: 18px; font-weight: bold; color: #dc2626;">${invoice.total_amount.toLocaleString('sv-SE')} kr</td>
            </tr>
          </table>
          
          <p><strong>Vänligen betala snarast möjligt.</strong></p>
          <p>Om du redan har betalat, vänligen bortse från denna påminnelse.</p>
          
          <div style="background: #f1f5f9; padding: 16px; border-radius: 8px; margin-top: 20px;">
            <p style="margin: 0 0 8px 0;"><strong>Betalningsinformation:</strong></p>
            <p style="margin: 0;">Bankgiro: 123-4567<br>OCR: ${invoice.invoice_number.replace(/-/g, '')}</p>
          </div>
          
          <p style="color: #64748b; font-size: 14px; margin-top: 30px;">
            Har du frågor? Kontakta styrelsen.<br><br>
            Med vänliga hälsningar,<br>
            Styrelsen
          </p>
        </div>
      `;

      await sendEmail(
        invoice.resident_email,
        `⚠️ Påminnelse: Obetald faktura ${invoice.invoice_number} - ${invoice.apartment_number}`,
        htmlContent,
        { fromName: organization?.name || 'Förvaltningen' }
      );

      // Update invoice with reminder sent
      await updateDocument('invoices', invoice.id, {
        reminder_sent_at: new Date().toISOString(),
        reminder_count: ((invoice as Invoice & { reminder_count?: number }).reminder_count || 0) + 1
      });

      toast.success(`Påminnelse skickad till ${invoice.resident_email}`);
      loadData();
    } catch (error) {
      toast.error('Kunde inte skicka påminnelse');
    }
  };

  // Send reminders to all overdue invoices
  const handleSendAllReminders = async () => {
    const overdueInvoices = invoices.filter(inv => inv.status === 'overdue' && inv.resident_email);
    
    if (overdueInvoices.length === 0) {
      toast.info('Inga förfallna fakturor med e-postadress');
      return;
    }

    if (!confirm(`Skicka påminnelse till ${overdueInvoices.length} förfallna fakturor?`)) {
      return;
    }

    let sent = 0;
    for (const invoice of overdueInvoices) {
      try {
        await handleSendReminder(invoice);
        sent++;
      } catch (error) {
        console.error('Error sending reminder:', error);
      }
    }

    toast.success(`${sent} påminnelser skickade`);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="invoicing-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Receipt size={28} className="text-primary" />
            Fakturering
          </h1>
          <p className="text-slate-500 mt-1">
            Hantera medlemsavgifter, rabatter och fakturor
          </p>
        </div>
        <div className="flex items-center gap-2">
          {invoices.filter(i => i.status === 'overdue').length > 0 && (
            <Button 
              variant="outline" 
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={handleSendAllReminders}
            >
              <Bell size={18} className="mr-2" />
              Skicka alla påminnelser ({invoices.filter(i => i.status === 'overdue').length})
            </Button>
          )}
          <Button onClick={() => setShowGenerateDialog(true)} data-testid="generate-invoices-btn">
            <Plus size={18} className="mr-2" />
            Generera fakturor
          </Button>
        </div>
      </div>

      {/* Overdue warning banner */}
      {invoices.filter(i => i.status === 'overdue').length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <Warning size={24} className="text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-red-800">
              {invoices.filter(i => i.status === 'overdue').length} förfallna fakturor
            </p>
            <p className="text-sm text-red-600">
              Totalt {formatCurrency(invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.total_amount, 0))} i obetalda förfallna fakturor
            </p>
          </div>
          <Button 
            size="sm"
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-100"
            onClick={handleSendAllReminders}
          >
            <Bell size={16} className="mr-1" />
            Skicka påminnelser
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.length}</p>
                <p className="text-sm text-slate-500">Totalt fakturor</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <PaperPlaneTilt size={24} className="text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'sent').length}</p>
                <p className="text-sm text-slate-500">Skickade</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'paid').length}</p>
                <p className="text-sm text-slate-500">Betalda</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Warning size={24} className="text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'overdue').length}</p>
                <p className="text-sm text-slate-500">Förfallna</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="invoices">Fakturor</TabsTrigger>
          <TabsTrigger value="history">Betalningshistorik</TabsTrigger>
          <TabsTrigger value="fees">Avgiftsmallar</TabsTrigger>
          <TabsTrigger value="adjustments">Rabatter & Pålägg</TabsTrigger>
          <TabsTrigger value="shared">Delade kostnader</TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card>
            <CardContent className="pt-6">
              {invoices.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Inga fakturor ännu</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowGenerateDialog(true)}
                  >
                    Generera din första faktura
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fakturanr</TableHead>
                      <TableHead>Lägenhet</TableHead>
                      <TableHead>Boende</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Belopp</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Förfallodatum</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map(invoice => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                        <TableCell>{invoice.apartment_number}</TableCell>
                        <TableCell>{invoice.resident_name}</TableCell>
                        <TableCell>{invoice.period}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(invoice.total_amount)}
                        </TableCell>
                        <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                        <TableCell>{new Date(invoice.due_date).toLocaleDateString('sv-SE')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {/* PDF Download - always available */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => generateInvoicePDF(invoice)}
                              title="Ladda ner PDF"
                            >
                              <FilePdf size={16} />
                            </Button>
                            
                            {/* Send invoice - only for drafts */}
                            {invoice.status === 'draft' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSendInvoice(invoice)}
                                title="Skicka faktura"
                              >
                                <EnvelopeSimple size={16} />
                              </Button>
                            )}
                            
                            {/* Mark as paid - for sent invoices */}
                            {invoice.status === 'sent' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMarkPaid(invoice)}
                                title="Markera betald"
                              >
                                <CheckCircle size={16} />
                              </Button>
                            )}
                            
                            {/* Send reminder - for overdue invoices */}
                            {invoice.status === 'overdue' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600"
                                  onClick={() => handleSendReminder(invoice)}
                                  title="Skicka påminnelse"
                                >
                                  <Bell size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleMarkPaid(invoice)}
                                  title="Markera betald"
                                >
                                  <CheckCircle size={16} />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Betalningshistorik per lägenhet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {apartments.map(apt => {
                  const aptInvoices = invoices.filter(inv => inv.apartment_id === apt.id);
                  const resident = getResidentForApartment(apt.id);
                  const totalPaid = aptInvoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total_amount, 0);
                  const totalUnpaid = aptInvoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.total_amount, 0);
                  const overdueCount = aptInvoices.filter(i => i.status === 'overdue').length;
                  
                  return (
                    <Card 
                      key={apt.id} 
                      className={`cursor-pointer hover:shadow-md transition-shadow ${overdueCount > 0 ? 'border-red-200' : ''}`}
                      onClick={() => {
                        setSelectedApartmentId(apt.id);
                        setShowHistoryDialog(true);
                      }}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-bold text-lg">{apt.apartment_number}</h3>
                            <p className="text-sm text-slate-500">{resident?.name || 'Ingen boende'}</p>
                          </div>
                          {overdueCount > 0 && (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                              {overdueCount} förfallen
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Antal fakturor:</span>
                            <span className="font-medium">{aptInvoices.length}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Totalt betalt:</span>
                            <span className="font-medium text-green-600">{formatCurrency(totalPaid)}</span>
                          </div>
                          {totalUnpaid > 0 && (
                            <div className="flex justify-between">
                              <span className="text-slate-500">Obetalt:</span>
                              <span className="font-medium text-red-600">{formatCurrency(totalUnpaid)}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-4 pt-3 border-t flex items-center justify-between">
                          <div className="flex gap-1">
                            {aptInvoices.slice(0, 5).map((inv) => (
                              <div 
                                key={inv.id || `${inv.period}-${inv.apartment_id}`}
                                className={`w-2 h-2 rounded-full ${
                                  inv.status === 'paid' ? 'bg-green-500' :
                                  inv.status === 'overdue' ? 'bg-red-500' :
                                  inv.status === 'sent' ? 'bg-blue-500' :
                                  'bg-slate-300'
                                }`}
                                title={`${inv.period}: ${inv.status}`}
                              />
                            ))}
                            {aptInvoices.length > 5 && (
                              <span className="text-xs text-slate-400">+{aptInvoices.length - 5}</span>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">Klicka för detaljer →</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {apartments.length === 0 && (
                <div className="text-center py-12">
                  <House size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Inga lägenheter registrerade</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fee Templates Tab */}
        <TabsContent value="fees">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Avgiftsmallar</CardTitle>
              <Button size="sm" onClick={() => { resetFeeForm(); setShowFeeDialog(true); }}>
                <Plus size={16} className="mr-1" />
                Ny mall
              </Button>
            </CardHeader>
            <CardContent>
              {feeTemplates.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500">Inga avgiftsmallar</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Namn</TableHead>
                      <TableHead>Grundbelopp</TableHead>
                      <TableHead>Per kvm</TableHead>
                      <TableHead>Frekvens</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feeTemplates.map(fee => (
                      <TableRow key={fee.id}>
                        <TableCell className="font-medium">{fee.name}</TableCell>
                        <TableCell>{formatCurrency(fee.base_amount)}</TableCell>
                        <TableCell>{fee.per_sqm_amount ? formatCurrency(fee.per_sqm_amount) : '-'}</TableCell>
                        <TableCell>
                          {fee.frequency === 'monthly' && 'Månadsvis'}
                          {fee.frequency === 'quarterly' && 'Kvartalsvis'}
                          {fee.frequency === 'yearly' && 'Årsvis'}
                          {fee.frequency === 'once' && 'Engångs'}
                        </TableCell>
                        <TableCell>
                          {fee.is_active ? (
                            <span className="text-green-600">Aktiv</span>
                          ) : (
                            <span className="text-slate-400">Inaktiv</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingFee(fee);
                                setFeeForm({
                                  name: fee.name,
                                  description: fee.description || '',
                                  base_amount: fee.base_amount.toString(),
                                  per_sqm_amount: fee.per_sqm_amount?.toString() || '',
                                  frequency: fee.frequency,
                                  is_active: fee.is_active
                                });
                                setShowFeeDialog(true);
                              }}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => handleDeleteFee(fee.id)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Adjustments Tab */}
        <TabsContent value="adjustments">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Rabatter & Pålägg</CardTitle>
              <Button size="sm" onClick={() => { resetAdjustmentForm(); setShowAdjustmentDialog(true); }}>
                <Plus size={16} className="mr-1" />
                Ny justering
              </Button>
            </CardHeader>
            <CardContent>
              {adjustments.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500">Inga justeringar</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Namn</TableHead>
                      <TableHead>Typ</TableHead>
                      <TableHead>Belopp</TableHead>
                      <TableHead>Frekvens</TableHead>
                      <TableHead>Gäller</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map(adj => (
                      <TableRow key={adj.id}>
                        <TableCell className="font-medium">{adj.name}</TableCell>
                        <TableCell>
                          {adj.type === 'discount' ? (
                            <span className="text-green-600">Rabatt</span>
                          ) : (
                            <span className="text-orange-600">Pålägg</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {adj.amount_type === 'percentage' 
                            ? `${adj.amount}%` 
                            : formatCurrency(adj.amount)}
                        </TableCell>
                        <TableCell>
                          {adj.frequency === 'recurring' ? 'Återkommande' : 'Engångs'}
                        </TableCell>
                        <TableCell>
                          {adj.applies_to === 'all' ? 'Alla' : `${adj.apartment_ids?.length || 0} lgh`}
                        </TableCell>
                        <TableCell>
                          {adj.is_active ? (
                            <span className="text-green-600">Aktiv</span>
                          ) : (
                            <span className="text-slate-400">Inaktiv</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingAdjustment(adj);
                                setAdjustmentForm({
                                  name: adj.name,
                                  description: adj.description || '',
                                  type: adj.type,
                                  amount_type: adj.amount_type,
                                  amount: adj.amount.toString(),
                                  frequency: adj.frequency,
                                  applies_to: adj.applies_to,
                                  apartment_ids: adj.apartment_ids || [],
                                  start_date: adj.start_date || '',
                                  end_date: adj.end_date || '',
                                  is_active: adj.is_active
                                });
                                setShowAdjustmentDialog(true);
                              }}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => handleDeleteAdjustment(adj.id)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shared Costs Tab */}
        <TabsContent value="shared">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Delade kostnader</CardTitle>
              <Button size="sm" onClick={() => { resetSharedCostForm(); setShowSharedCostDialog(true); }}>
                <Plus size={16} className="mr-1" />
                Ny kostnad
              </Button>
            </CardHeader>
            <CardContent>
              {sharedCosts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500">Inga delade kostnader</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Namn</TableHead>
                      <TableHead>Totalbelopp</TableHead>
                      <TableHead>Fördelning</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Inkluderas</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sharedCosts.map(cost => (
                      <TableRow key={cost.id}>
                        <TableCell className="font-medium">{cost.name}</TableCell>
                        <TableCell>{formatCurrency(cost.total_amount)}</TableCell>
                        <TableCell>
                          {cost.distribution_method === 'equal' && 'Lika'}
                          {cost.distribution_method === 'by_sqm' && 'Per kvm'}
                          {cost.distribution_method === 'by_shares' && 'Per andel'}
                        </TableCell>
                        <TableCell>{cost.period}</TableCell>
                        <TableCell>
                          {cost.is_included_in_invoice ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : (
                            <X size={18} className="text-slate-400" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingSharedCost(cost);
                                setSharedCostForm({
                                  name: cost.name,
                                  description: cost.description || '',
                                  total_amount: cost.total_amount.toString(),
                                  distribution_method: cost.distribution_method,
                                  period: cost.period,
                                  is_included_in_invoice: cost.is_included_in_invoice
                                });
                                setShowSharedCostDialog(true);
                              }}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => handleDeleteSharedCost(cost.id)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs using extracted components */}
      <FeeTemplateDialog
        open={showFeeDialog}
        onOpenChange={setShowFeeDialog}
        editingFee={editingFee}
        form={feeForm}
        onFormChange={setFeeForm}
        onSave={handleSaveFee}
      />

      <AdjustmentDialog
        open={showAdjustmentDialog}
        onOpenChange={setShowAdjustmentDialog}
        editingAdjustment={editingAdjustment}
        form={adjustmentForm}
        onFormChange={setAdjustmentForm}
        onSave={handleSaveAdjustment}
        apartments={apartments}
      />

      <SharedCostDialog
        open={showSharedCostDialog}
        onOpenChange={setShowSharedCostDialog}
        editingSharedCost={editingSharedCost}
        form={sharedCostForm}
        onFormChange={setSharedCostForm}
        onSave={handleSaveSharedCost}
      />

      <GenerateInvoicesDialog
        open={showGenerateDialog}
        onOpenChange={setShowGenerateDialog}
        form={generateForm}
        onFormChange={setGenerateForm}
        onPreview={handleGeneratePreview}
        feeTemplates={feeTemplates}
        apartmentCount={apartments.length}
      />

      <InvoicePreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        previewInvoices={previewInvoices}
        onCreateInvoices={handleCreateInvoices}
      />

      <PaymentHistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        apartmentId={selectedApartmentId}
        apartments={apartments}
        invoices={invoices}
        getResidentForApartment={getResidentForApartment}
        onDownloadPdf={generateInvoicePDF}
        onSendReminder={handleSendReminder}
        onMarkPaid={handleMarkPaid}
      />
    </div>
  );
}
